import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
  HTTP_INTERCEPTORS,
} from '@angular/common/http';
import { Observable, catchError, map, switchMap, throwError } from 'rxjs';
import { AuthenticationService } from './apps/Authentication/authentication.service';
import { environment } from 'src/environments/environment';

@Injectable()
export class AuthTenantInterceptor implements HttpInterceptor {
  private readonly apiBase = environment.apiBaseUrl; // [auth-mt]
  private readonly tokenUrl = `${environment.apiBaseUrl}/connect/token`; // [auth-mt]
  private readonly revokeUrl = `${environment.apiBaseUrl}/connect/revoke`; // [auth-mt]
  private readonly meUrl = `${environment.apiBaseUrl}/api/auth/me`; // [auth-mt]

  constructor(private auth: AuthenticationService) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const isTokenEndpoint = req.url.startsWith(this.tokenUrl); // [auth-mt]
    const isRevokeEndpoint = req.url.startsWith(this.revokeUrl); // [auth-mt]
    const isAuthMe = req.url.startsWith(this.meUrl); // [auth-mt]
    // /connect/revoke es OpenIddict, igual que /connect/token: sin Authorization ni X-Tenant-Id.
    const isApiCall = req.url.startsWith(this.apiBase) && !isTokenEndpoint && !isRevokeEndpoint; // [auth-mt]

    let authReq = req;
    try { if (!environment.production) { console.log('[auth-mt][debug] intercept url:', req.url, 'isApiCall:', isApiCall, 'isTokenEndpoint:', isTokenEndpoint); } } catch { }

    // [auth-mt] Add Authorization if we have a token and this isn't an OpenIddict endpoint
    const token = this.auth.getAccessToken();
    if (token && !isTokenEndpoint && !isRevokeEndpoint) {
      authReq = authReq.clone({
        setHeaders: { Authorization: `Bearer ${token}` },
      });
      try { if (!environment.production) { console.log('[auth-mt][debug] Attached Authorization header'); } } catch { }
    }

    // [auth-mt] Always attach X-Tenant-Id to protected API calls
    if (isApiCall && !isAuthMe) {
      const tenantRaw = this.auth.getTenantId() as any;
      if (tenantRaw != null && tenantRaw !== 'null' && tenantRaw !== 'undefined') {
        const tenant = String(tenantRaw);
        try { if (!environment.production) { console.debug('[auth-mt] attach X-Tenant-Id:', tenant); } } catch { }
        authReq = authReq.clone({
          setHeaders: { 'X-Tenant-Id': tenant },
        });
      } else {
        try { if (!environment.production) { console.warn('[auth-mt][debug] Skipping X-Tenant-Id, tenant is', tenantRaw); } } catch { }
      }
    }

    return next.handle(authReq).pipe(
      // Mapear requestResult a body para compatibilidad con el frontend
      map((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse && event.body && event.body.requestResult !== undefined && event.body.body === undefined) {
          const clonedBody = { ...event.body, body: event.body.requestResult };
          return event.clone({ body: clonedBody });
        }
        return event;
      }),
      catchError((err: HttpErrorResponse) => {
        if (err.status === 401 && !isRevokeEndpoint && !authReq.headers.has('x-auth-retried')) {
          try { if (!environment.production) { console.warn('[auth-mt][debug] 401 received, attempting refresh once'); } } catch { }
          // [auth-mt] Try to refresh once
          return this.auth.refresh().pipe(
            switchMap((ok: boolean) => {
              if (!ok) {
                this.auth.logout();
                try { if (!environment.production) { console.warn('[auth-mt][debug] Refresh failed; logging out'); } } catch { }
                return throwError(() => err);
              }
              const newToken = this.auth.getAccessToken();
              const headers: Record<string, string> = {
                Authorization: newToken ? `Bearer ${newToken}` : '',
                'x-auth-retried': '1', // [auth-mt] custom header to mark retried
              };

              // [auth-mt] Re-agregar X-Tenant-Id en el retry si es necesario
              if (isApiCall && !isAuthMe) {
                const tenantRaw = this.auth.getTenantId() as any;
                if (tenantRaw != null && tenantRaw !== 'null' && tenantRaw !== 'undefined') {
                  headers['X-Tenant-Id'] = String(tenantRaw);
                }
              }

              const retried = authReq.clone({
                setHeaders: headers,
              });
              try { if (!environment.production) { console.log('[auth-mt][debug] Retrying request after refresh with headers:', Object.keys(headers)); } } catch { }
              return next.handle(retried);
            })
          );
        }

        if (err.status === 403) {
          // [auth-mt] Do not retry on tenant mismatch; bubble up with clearer message
          try { if (!environment.production) { console.warn('[auth-mt][debug] 403 Acceso denegado (tenant)'); } } catch { }
          return throwError(() => err);
        }

        return throwError(() => err);
      })
    );
  }
}

// [auth-mt] Provider helper for app.config.ts
export const AUTH_TENANT_INTERCEPTOR_PROVIDER = {
  provide: HTTP_INTERCEPTORS,
  useClass: AuthTenantInterceptor,
  multi: true,
};
